import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { defaultContent } from "../data/defaultContent";
import { fetchPublishedNews, fetchSiteContent } from "../lib/content";
import type { NewsPost, SiteContent } from "../types";

interface ContentContextValue {
  content: SiteContent;
  news: NewsPost[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const ContentContext = createContext<ContentContextValue | null>(null);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent>(defaultContent);
  const [news, setNews] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [nextContent, nextNews] = await Promise.all([fetchSiteContent(), fetchPublishedNews()]);
      setContent(nextContent);
      setNews(nextNews);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh().catch(() => {
      // The default content remains available when the remote source is temporarily unreachable.
    });
  }, []);

  const value = useMemo(() => ({ content, news, loading, refresh }), [content, news, loading]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const value = useContext(ContentContext);
  if (!value) throw new Error("useContent must be used within ContentProvider");
  return value;
}
